import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Menu, Layout } from 'antd';
import * as Colors from '@ant-design/colors';
import { MenuFoldOutlined } from '@ant-design/icons';

// components
import { RouteMatcher, routeMatchers, useRouteMatcher } from '../../hooks/bread-crumbs';
import { useDeviceSize } from '../../hooks/device-size';
import { txt } from '../../locales/i18n';
import { media } from '../../context/media';

function getMenuItems(): RouteMatcher[] {
	const authorizedRoutesStore: string | null = localStorage.getItem('bb_authorized_routes');
	if (authorizedRoutesStore === null) return [];

	const menuItems: RouteMatcher[] = routeMatchers.filter(({ menuItem }: RouteMatcher) => {
		return menuItem === true;
	});

	const authorizedRoutes: string[] = JSON.parse(authorizedRoutesStore);
	if (authorizedRoutes.includes('*')) return menuItems;

	const authorizedMenuItems: RouteMatcher[] = menuItems.filter(
		({ baseUrl, menuItem }: RouteMatcher) => {
			return authorizedRoutes.includes(baseUrl);
		}
	);

	return authorizedMenuItems;
}

export function Sidebar({ visible, setVisible }: any): JSX.Element {
	const [height, setHeight] = useState<number>(window.innerHeight);

	const history = useHistory();
	const isMobileDevice = useDeviceSize();
	const routeMatcher = useRouteMatcher();

	useEffect(() => {
		function handleResize() {
			const windowHeight = window.innerHeight;
			setHeight(windowHeight);
		}

		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [setVisible]);

	const handleNavigate = (baseUrl: string) => {
		if (isMobileDevice) setVisible(false);
		history.push(baseUrl);
	};

	return (
		<StyledSidebar className="sidebar" height={height} isVisible={visible}>
			<div className="sidebar-container">
				<Layout.Sider collapsed={visible === false} tabIndex={0}>
					<Menu selectedKeys={[routeMatcher?.baseUrl ?? '']} theme="dark" mode="inline">
						{getMenuItems().map(({ name, icon, baseUrl }: RouteMatcher) => (
							<Menu.Item
								key={baseUrl}
								icon={icon}
								onClick={() => handleNavigate(baseUrl)}
							>
								{isMobileDevice && visible === false ? null : name}
							</Menu.Item>
						))}

						{visible === true && (
							<Menu.Item
								key="menu-toggle-button"
								className="menu-toggle-button"
								icon={<MenuFoldOutlined />}
								onClick={() => setVisible(false)}
							>
								{txt('global.hide')}
							</Menu.Item>
						)}
					</Menu>
				</Layout.Sider>
			</div>

			{isMobileDevice && visible && (
				<span className="overlay" onClick={() => setVisible(false)} />
			)}
		</StyledSidebar>
	);
}

type StyledSiderProps = {
	isVisible: boolean;
	height: number;
};

const StyledSidebar = styled.div<StyledSiderProps>`
	aside {
		outline: none;
		height: ${({ height }) => height}px;

		ul {
			display: flex;
			flex-direction: column;
			height: 100%;

			* {
				font-size: 0.8rem;
			}

			.menu-toggle-button {
				margin-top: auto;
				color: ${Colors.blue.primary};
			}
		}
	}

	.ant-layout-sider {
		flex: 0 0 165px !important;
		max-width: 165px !important;
		min-width: 165px !important;
		width: 165px !important;
	}

	.ant-layout-sider-collapsed {
		flex: 0 0 60px !important;
		max-width: 60px !important;
		min-width: 60px !important;
		width: 60px !important;
	}

	span.overlay {
		position: absolute;
		inset: 0;
		z-index: 10;
		background-color: #3399ff33;
		backdrop-filter: blur(6px);
	}

	@media only screen and (max-width: ${media.phoneMaxWidth}px) {
		.sidebar-container {
			position: absolute;
			top: 0;
			left: 0;
			transition: left 300ms ease-in-out;
			left: ${({ isVisible }) => (isVisible ? '0' : '-100%')};
		}

		aside {
			position: absolute;
			height: 100%;
			top: 0;
			left: 0;
			z-index: 100;
		}

		.ant-layout-sider {
			flex: 0 0 200px !important;
			max-width: 200px !important;
			min-width: 200px !important;
			width: 200px !important;
		}
	}
`;
